// src/pages/Team/TeamPersonsList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Person } from "../Admin/ID/types";
import "../../Pages/Admin/Event/AdminEvent.css";

interface Props {
    persons: Person[];
    onEdit: (personId: number) => void;
    onDelete: (personId: number) => void;
    tableId?: string;
    onSelectPersons?: (selected: Person[]) => void;
    currentEventId?: number;
}

const TeamPersonsList: React.FC<Props> = ({
    persons,
    onEdit,
    onDelete,
    tableId,
    onSelectPersons,
    currentEventId,
}) => {
    const itemsPerPage = 50;
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        setCurrentPage(1);
        setSelectedIds([]);
        onSelectPersons?.([]);
    }, [persons]);

    const totalPages = Math.ceil(persons.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const currentPersons = useMemo(() => persons.slice(startIndex, startIndex + itemsPerPage), [persons, startIndex]);

    const handleSelect = (personId: number, checked: boolean) => {
        const updated = checked ? [...selectedIds, personId] : selectedIds.filter(id => id !== personId);
        setSelectedIds(updated);
        onSelectPersons?.(
            persons.filter(p => updated.includes(p.personId))
        );
    };

    const handleSelectAll = (checked: boolean) => {
        const pageIds = currentPersons.map(p => p.personId);
        const updated = checked ? Array.from(new Set([...selectedIds, ...pageIds])) : selectedIds.filter(id => !pageIds.includes(id));
        setSelectedIds(updated);
        onSelectPersons?.(
            persons.filter(p => updated.includes(p.personId))
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(page => page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2));

        return (
            <div className="pagination-wrapper">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="pagination-btn prev-next">Previous</button>
                {pages.map((page, index) => {
                    const prev = pages[index - 1];
                    const gap = index > 0 && page - prev > 1;
                    return (
                        <React.Fragment key={`page-${page}`}>
                            {gap && <span className="pagination-ellipsis">...</span>}
                            <button className={`pagination-btn page-number ${page === currentPage ? "active" : ""}`} onClick={() => setCurrentPage(page)}>{page}</button>
                        </React.Fragment>
                    );
                })}
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="pagination-btn prev-next">Next</button>
            </div>
        );
    };

    return (
        <div className="group-table-container">
            {renderPagination()}
            <div className="nice-table-wrap">
                <table id={tableId} className="event-table event-table-modern nice-table">
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}>
                                <input type="checkbox" checked={currentPersons.length > 0 && currentPersons.every(p => selectedIds.includes(p.personId))} onChange={e => handleSelectAll(e.target.checked)} />
                            </th>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Country</th>
                            <th>Title</th>
                            <th>Company</th>
                            <th>Events</th>
                            <th>Functions</th>
                            <th className="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentPersons.length > 0 ? currentPersons.map(p => {
                            const currentPersonEvent = p.events?.find((e: any) =>
                                Number(e.eventID ?? e.eventId) === Number(currentEventId)
                            );
                            const currentFunction =
                                p.functions
                                    ?.filter(f =>
                                        !currentEventId || Number(f.eventId) === Number(currentEventId)
                                    )
                                    .map(f => f.functionName)
                                    .join(", ") || "-";

                            return (
                                <tr key={p.personId}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(p.personId)}
                                            onChange={e => handleSelect(p.personId, e.target.checked)}
                                        />
                                    </td>
                                    <td>{p.personId}</td>
                                    <td>{p.firstName} {p.lastName}</td>
                                    <td>{p.country ?? "-"}</td>
                                    <td>{p.title ?? "-"}</td>
                                    <td>{p.company ?? "-"}</td>
                                    <td>
                                        {currentPersonEvent ? currentPersonEvent.eventName : "-"}
                                    </td>
                                    <td>
                                        {currentFunction ?? "-"}
                                    </td>
                                    <td>
                                        <button onClick={() => onEdit(p.personId)}>Edit</button>
                                        <button onClick={() => { if (window.confirm("Delete this person?")) onDelete(p.personId); }}>Delete</button>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan={7}>No Persons Found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {renderPagination()}
        </div>
    );
};

export default TeamPersonsList;